import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import AIOverview from 'app/views/settings/ai/AIOverview';
import AIFaq from 'app/views/settings/ai/AIFaq';
import ModelsAndCredentials from 'app/views/settings/ai/ModelsAndCredentials';
import AIContextSettings from 'app/views/settings/ai/AIContextSettings';
import CommandAccess from 'app/views/settings/ai/CommandAccess';
import AdvisorUsage from 'app/views/settings/ai/AdvisorUsage';
import McpServersTab from 'app/views/settings/ai/McpServersTab';
import InfobloxIqTab from 'app/views/settings/ai/InfobloxIqTab';

import { Box, Divider, Flex, Tab, Tabs } from 'core/components';
import { inject, observer } from 'mobx-react';
import Page from 'app/components/page/Page';
import PageHeading from 'app/components/page/PageHeading';
import { Classes } from '@blueprintjs/core';
import styled from 'styled-components';
import { rgba } from 'polished';
import withUrlPathTabs from 'core/components/withUrlPathTabs';
import RunbookTable from 'app/views/settings/ai/RunbookTable';
import AutoInvestigationTable from 'app/views/settings/ai/AutoInvestigationTable';
import $runbook from 'app/stores/runbook/$runbook';
import {
  KENTIK_AI_SETTINGS_BASE_URL,
  canAccessBuiltinIntegrations,
  getKentikAiSettingsTabConfigs,
  isKentikAiRunbooksOnly,
  isKentikAiSettingsTabDisabled
} from 'app/views/settings/ai/kentikAiSettingsTabs';

const ThemedDivider = styled(Divider)`
  border-bottom: ${({ theme }) => theme.borders.thinLight};
`;

const StyledTabs = styled(Tabs)`
  min-width: 0;

  .${Classes.TAB_LIST} {
    border-right: ${({ theme }) => theme.borders.thinLight};
    width: 260px;
    padding: 8px 16px 0 16px;

    > .${Classes.TAB} {
      color: ${({ theme }) => theme.colors.body};
      padding: 2px 16px;
      margin-top: 8px;

      .${Classes.ICON} {
        color: ${({ theme }) => theme.colors.muted};
      }

      &[aria-selected='true'] {
        font-weight: 700;
      }

      &[aria-disabled='true'] {
        color: ${({ theme }) => theme.colors.muted};
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    > .${Classes.TAB_INDICATOR_WRAPPER} {
      > .${Classes.TAB_INDICATOR} {
        background-color: ${({ theme }) => rgba(theme.colors.lightGray5, theme.name === 'light' ? 1 : 0.15)};
      }
    }
  }

  .${Classes.TAB_PANEL} {
    flex: 1;
    overflow-y: auto;
    padding-right: 16px;
    height: 100%;
  }
`;

const UrlPathTabs = withUrlPathTabs(StyledTabs);

@inject('$auth')
@observer
class KentikAiSettings extends Component {
  get activeTab() {
    const { match } = this.props;
    return match.params.tab;
  }

  get isRunbooksOnly() {
    const { $auth } = this.props;
    return isKentikAiRunbooksOnly($auth);
  }

  // Runbooks visibility/access is governed purely by RBAC, independent of isRunbooksOnly/user_level.
  get hasRunbooksPermission() {
    return $runbook.canReadRunbooks;
  }

  tabs() {
    const { $auth } = this.props;

    const panels = {
      overview: <AIOverview />,
      'models-and-credentials': <ModelsAndCredentials />,
      context: <AIContextSettings />,
      'nms-command-access': <CommandAccess />,
      runbooks: <RunbookTable />,
      'auto-investigation': <AutoInvestigationTable />,
      'mcp-servers': <McpServersTab />,
      'infoblox-iq': <InfobloxIqTab />,
      'privacy-and-security': <AIFaq />,
      usage: <AdvisorUsage />
    };

    return getKentikAiSettingsTabConfigs($auth)
      .filter((tab) => tab.condition)
      .map(({ condition, ...tab }) => ({
        ...tab,
        panel: panels[tab.id],
        disabled: isKentikAiSettingsTabDisabled($auth, tab.id)
      }));
  }

  getFallbackTabId(tabsList) {
    const accessibleTab = tabsList.find((tab) => !tab.disabled);
    return accessibleTab ? accessibleTab.id : null;
  }

  get baseUrl() {
    const { baseUrl } = this.props;
    return baseUrl || KENTIK_AI_SETTINGS_BASE_URL;
  }

  get defaultTabId() {
    const { $auth } = this.props;

    if (!this.isRunbooksOnly) {
      return 'overview';
    }

    if (this.hasRunbooksPermission) {
      return 'runbooks';
    }

    return canAccessBuiltinIntegrations($auth) ? 'infoblox-iq' : 'overview';
  }

  // Renders just the active panel, for hosts (e.g. AI Advisor) that provide their own navigation
  embeddedContent() {
    const activeTab = this.tabs().find((tab) => tab.id === this.activeTab && !tab.disabled);

    if (!activeTab) {
      return <Redirect to={`${this.baseUrl}/${this.defaultTabId}`} />;
    }

    return (
      <Box flex="1 1 0%" minHeight={0} overflow="auto">
        {activeTab.panel}
      </Box>
    );
  }

  pageContent() {
    const { embedded } = this.props;
    const tabs = this.tabs();
    const activeTab = tabs.find((tab) => tab.id === this.activeTab);

    // Block direct navigation to a disabled (RBAC-inaccessible) tab route, e.g. via URL.
    if (this.activeTab && (!activeTab || activeTab.disabled)) {
      const fallbackTabId = this.getFallbackTabId(tabs);
      if (fallbackTabId && fallbackTabId !== this.activeTab) {
        return <Redirect to={`${this.baseUrl}/${fallbackTabId}`} />;
      }
    }

    if (embedded) {
      return this.embeddedContent();
    }

    const pageProps = activeTab
      ? { title: activeTab.title, parentLinks: [{ link: this.baseUrl, label: 'Kentik AI' }] }
      : { title: 'Kentik AI' };

    return (
      <Page {...pageProps} px={0} pb={0}>
        <Flex justifyContent="space-between" m={2} mt={1} alignItems="center">
          <PageHeading level={4} ml={2} title="Kentik AI Settings" hideFavorite />
        </Flex>

        <ThemedDivider width="100%" />
        <Box height="calc(100vh - 170px)">
          <UrlPathTabs
            vertical
            selectedTabId={this.activeTab}
            defaultSelectedTabId={this.defaultTabId}
            useUrlPathParam
            renderActiveTabPanelOnly
            outerFlexStyles={{ m: 0, height: '100%' }}
            flexed
          >
            {tabs.map((tabInfo) => (
              <Tab {...tabInfo} key={tabInfo.id} />
            ))}
          </UrlPathTabs>
        </Box>
      </Page>
    );
  }

  render() {
    return this.pageContent();
  }
}

export default KentikAiSettings;
